/**
 * SEO Meta Tags Manager
 * Handles dynamic meta tags, Open Graph, Twitter Cards, and more
 */

/**
 * Default site metadata
 */
const DEFAULT_METADATA = {
  siteName: {
    fa: 'لاویسا - فروشگاه آنلاین مد و پوشاک',
    en: 'Lavvissa - Online Fashion Store',
  },
  siteUrl: process.env.REACT_APP_SITE_URL || 'https://lavvissa.com',
  defaultTitle: {
    fa: 'لاویسا - اینجا راحت خرید کن!',
    en: 'Lavvissa - Shop with Ease!',
  },
  defaultDescription: {
    fa: 'لاویسا، فروشگاه آنلاین مد و پوشاک با بهترین برندهای ترکیه و جهان. خرید آسان، ارسال سریع و تضمین کیفیت.',
    en: 'Lavvissa - Online fashion store featuring top Turkish and international brands. Easy shopping, fast delivery, quality guaranteed.',
  },
  defaultImage: '/landing.png',
  twitterHandle: '@lavvissa',
  facebookAppId: process.env.REACT_APP_FACEBOOK_APP_ID,
};

const normalizeMetaLanguage = (language) =>
  String(language || "fa").toLowerCase().startsWith("en") ? "en" : "fa";

/**
 * Updates document title
 * @param {string} title - Page title
 * @param {string} language - Current language
 */
export const updateTitle = (title, language = 'fa') => {
  const safeLanguage = normalizeMetaLanguage(language);
  if (!title) {
    document.title = DEFAULT_METADATA.defaultTitle[safeLanguage];
    return;
  }

  const siteName = DEFAULT_METADATA.siteName[safeLanguage];
  document.title = `${title} | ${siteName}`;
};

/**
 * Creates or updates a meta tag
 * @param {string} name - Meta tag name or property
 * @param {string} content - Meta tag content
 * @param {boolean} isProperty - Whether to use property attribute instead of name
 */
export const setMetaTag = (name, content, isProperty = false) => {
  if (!content) return;

  const attribute = isProperty ? 'property' : 'name';
  let meta = document.querySelector(`meta[${attribute}="${name}"]`);

  if (!meta) {
    meta = document.createElement('meta');
    meta.setAttribute(attribute, name);
    document.head.appendChild(meta);
  }

  meta.setAttribute('content', content);
};

/**
 * Removes a meta tag
 * @param {string} name - Meta tag name or property
 * @param {boolean} isProperty - Whether it's a property attribute
 */
export const removeMetaTag = (name, isProperty = false) => {
  const attribute = isProperty ? 'property' : 'name';
  const meta = document.querySelector(`meta[${attribute}="${name}"]`);
  if (meta) meta.remove();
};

/**
 * Sets basic SEO meta tags
 * @param {Object} meta - Meta information
 * @param {string} language - Current language
 */
export const setBasicMeta = (meta = {}, language = 'fa') => {
  const safeLanguage = normalizeMetaLanguage(language);
  const {
    title,
    description,
    keywords = [],
    author,
    robots = 'index, follow',
    canonical,
  } = meta;

  // Title
  updateTitle(title, safeLanguage);

  // Description
  const desc = description || DEFAULT_METADATA.defaultDescription[safeLanguage];
  setMetaTag('description', desc);

  // Keywords
  if (keywords.length > 0) {
    setMetaTag('keywords', keywords.join(', '));
  }

  // Author
  if (author) {
    setMetaTag('author', author);
  }

  // Robots
  setMetaTag('robots', robots);

  // Canonical URL
  if (canonical) {
    let link = document.querySelector('link[rel="canonical"]');
    if (!link) {
      link = document.createElement('link');
      link.rel = 'canonical';
      document.head.appendChild(link);
    }
    link.href = canonical;
  }

  // Language
  document.documentElement.lang = safeLanguage;
  document.documentElement.dir = safeLanguage === 'fa' ? 'rtl' : 'ltr';
};

/**
 * Sets Open Graph meta tags (Facebook, LinkedIn, etc.)
 * @param {Object} og - Open Graph data
 * @param {string} language - Current language
 */
export const setOpenGraphMeta = (og = {}, language = 'fa') => {
  const {
    title,
    description,
    image,
    url,
    type = 'website',
    siteName,
    locale,
  } = og;

  const siteUrl = DEFAULT_METADATA.siteUrl;
  const defaultTitle = DEFAULT_METADATA.defaultTitle[language];
  const defaultDescription = DEFAULT_METADATA.defaultDescription[language];
  const defaultSiteName = DEFAULT_METADATA.siteName[language];

  setMetaTag('og:type', type, true);
  setMetaTag('og:title', title || defaultTitle, true);
  setMetaTag('og:description', description || defaultDescription, true);
  setMetaTag('og:site_name', siteName || defaultSiteName, true);

  // Image
  const ogImage = image || `${siteUrl}${DEFAULT_METADATA.defaultImage}`;
  setMetaTag('og:image', ogImage, true);
  setMetaTag('og:image:width', '1200', true);
  setMetaTag('og:image:height', '630', true);
  setMetaTag('og:image:alt', title || defaultTitle, true);

  // URL
  if (url) {
    setMetaTag('og:url', `${siteUrl}${url}`, true);
  }

  // Locale
  const ogLocale = locale || (language === 'fa' ? 'fa_IR' : 'en_US');
  setMetaTag('og:locale', ogLocale, true);

  // Alternate locales
  if (language === 'fa') {
    setMetaTag('og:locale:alternate', 'en_US', true);
  } else {
    setMetaTag('og:locale:alternate', 'fa_IR', true);
  }

  // Facebook App ID
  if (DEFAULT_METADATA.facebookAppId) {
    setMetaTag('fb:app_id', DEFAULT_METADATA.facebookAppId, true);
  }
};

/**
 * Sets Twitter Card meta tags
 * @param {Object} twitter - Twitter card data
 * @param {string} language - Current language
 */
export const setTwitterMeta = (twitter = {}, language = 'fa') => {
  const {
    title,
    description,
    image,
    card = 'summary_large_image',
    site,
    creator,
  } = twitter;

  const siteUrl = DEFAULT_METADATA.siteUrl;
  const defaultTitle = DEFAULT_METADATA.defaultTitle[language];
  const defaultDescription = DEFAULT_METADATA.defaultDescription[language];

  setMetaTag('twitter:card', card);
  setMetaTag('twitter:title', title || defaultTitle);
  setMetaTag('twitter:description', description || defaultDescription);

  // Image
  const twitterImage = image || `${siteUrl}${DEFAULT_METADATA.defaultImage}`;
  setMetaTag('twitter:image', twitterImage);
  setMetaTag('twitter:image:alt', title || defaultTitle);

  // Site and creator
  setMetaTag('twitter:site', site || DEFAULT_METADATA.twitterHandle);
  if (creator) {
    setMetaTag('twitter:creator', creator);
  }
};

/**
 * Sets product-specific meta tags
 * @param {Object} product - Product data
 * @param {string} language - Current language
 */
export const setProductMeta = (product, language = 'fa') => {
  const title = product.name?.[language] || product.name?.fa;
  const description = product.description?.[language] || product.description?.fa;
  const price = product.pricing?.basePrice?.toman || product.pricing?.basePrice?.TL;
  const currency = product.pricing?.basePrice?.toman ? 'IRR' : 'TRY';
  const image = product.media?.featuredImage;
  const availability = product.stock?.isInStock || product.sourceInfo?.isAvailable ? 'in stock' : 'out of stock';
  const brand = product.brand?.name;

  // Basic meta
  setBasicMeta({ title, description }, language);

  // Open Graph for products
  setOpenGraphMeta({
    title,
    description,
    image,
    type: 'product',
    url: `/product/${product.slug}`,
  }, language);

  // Product-specific OG tags
  if (price) {
    setMetaTag('product:price:amount', price.toString(), true);
    setMetaTag('product:price:currency', currency, true);
  }

  if (brand) {
    setMetaTag('product:brand', brand, true);
  }

  setMetaTag('product:availability', availability, true);

  if (product.categories && product.categories.length > 0) {
    setMetaTag('product:category', product.categories[0], true);
  }

  // Twitter card for products
  setTwitterMeta({
    title,
    description,
    image,
    card: 'summary_large_image',
  }, language);

  // Additional product meta
  if (product.sku) {
    setMetaTag('product:retailer_item_id', product.sku, true);
  }
};

/**
 * Sets hreflang tags for multilingual SEO
 * @param {string} currentPath - Current page path
 * @param {Array<string>} languages - Available languages
 */
export const setHreflangTags = (currentPath, languages = ['fa', 'en']) => {
  const siteUrl = DEFAULT_METADATA.siteUrl;

  // Remove existing hreflang tags
  document.querySelectorAll('link[rel="alternate"][hreflang]').forEach(link => link.remove());

  languages.forEach((lang) => {
    const link = document.createElement('link');
    link.rel = 'alternate';
    link.hreflang = lang === 'fa' ? 'fa-IR' : 'en-US';
    link.href = `${siteUrl}/${lang}${currentPath}`;
    document.head.appendChild(link);
  });

  // x-default for international targeting
  const defaultLink = document.createElement('link');
  defaultLink.rel = 'alternate';
  defaultLink.hreflang = 'x-default';
  defaultLink.href = `${siteUrl}${currentPath}`;
  document.head.appendChild(defaultLink);
};

/**
 * Sets mobile-specific meta tags
 */
export const setMobileMeta = () => {
  // Viewport (already in HTML, but ensure it's set)
  setMetaTag('viewport', 'width=device-width, initial-scale=1, maximum-scale=5, user-scalable=yes');

  // Mobile web app capable
  setMetaTag('mobile-web-app-capable', 'yes');
  setMetaTag('apple-mobile-web-app-capable', 'yes');
  setMetaTag('apple-mobile-web-app-status-bar-style', 'black-translucent');

  // Apple touch icon
  let appleIcon = document.querySelector('link[rel="apple-touch-icon"]');
  if (!appleIcon) {
    appleIcon = document.createElement('link');
    appleIcon.rel = 'apple-touch-icon';
    appleIcon.href = '/lavvissa-logo.png';
    document.head.appendChild(appleIcon);
  }

  // Theme color
  setMetaTag('theme-color', '#000000');
  setMetaTag('msapplication-TileColor', '#000000');
};

/**
 * Complete meta tags setup for a page
 * @param {Object} pageData - Page metadata
 * @param {string} language - Current language
 */
export const setPageMeta = (pageData = {}, language = 'fa') => {
  const {
    title,
    description,
    keywords,
    image,
    url,
    type = 'website',
    product, // If it's a product page
    noIndex = false,
  } = pageData;

  // Handle product pages separately
  if (product) {
    setProductMeta(product, language);
    return;
  }

  // Basic SEO
  setBasicMeta({
    title,
    description,
    keywords,
    robots: noIndex ? 'noindex, nofollow' : 'index, follow',
    canonical: url ? `${DEFAULT_METADATA.siteUrl}${url}` : undefined,
  }, language);

  // Open Graph
  setOpenGraphMeta({
    title,
    description,
    image,
    url,
    type,
  }, language);

  // Twitter Card
  setTwitterMeta({
    title,
    description,
    image,
  }, language);

  // Hreflang
  if (url) {
    setHreflangTags(url);
  }

  // Mobile meta
  setMobileMeta();
};

/**
 * Clears all dynamic meta tags (for SPA navigation)
 */
export const clearDynamicMeta = () => {
  // Remove Open Graph tags
  document.querySelectorAll('meta[property^="og:"]').forEach(meta => meta.remove());

  // Remove Twitter tags
  document.querySelectorAll('meta[name^="twitter:"]').forEach(meta => meta.remove());

  // Remove product tags
  document.querySelectorAll('meta[property^="product:"]').forEach(meta => meta.remove());

  // Remove hreflang
  document.querySelectorAll('link[rel="alternate"][hreflang]').forEach(link => link.remove());

  // Remove canonical
  const canonical = document.querySelector('link[rel="canonical"]');
  if (canonical) canonical.remove();
};

const MetaTags = {
  updateTitle,
  setMetaTag,
  removeMetaTag,
  setBasicMeta,
  setOpenGraphMeta,
  setTwitterMeta,
  setProductMeta,
  setHreflangTags,
  setMobileMeta,
  setPageMeta,
  clearDynamicMeta,
  DEFAULT_METADATA,
};

export default MetaTags;
